import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { SESSION_MODES, SESSION_STATUS, filterByWords, orderSessions, sessionWhen, trainingError } from "../../lib/training";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, ChevronRight, Download, Loader2, RefreshCw, Users } from "lucide-react";

const VIEWS = [["open", "À venir et en cours", ["prepared", "in_progress"]], ["closed", "Clôturées", ["closed"]], ["cancelled", "Annulées", ["cancelled"]]];

export function SessionStatus({ status }) {
  const meta = SESSION_STATUS[status] || SESSION_STATUS.prepared;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`} data-testid="session-status">{meta.label}</span>;
}

// One line of a session list: shared by the Formations page and the mission / pharmacy / collaborator cards.
export function SessionRow({ session, showPharmacy = true }) {
  return (
    <Link to={`/formations/sessions/${session.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`training-session-row-${session.reference}`}>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 break-words">{session.program.label}</span>
          <SessionStatus status={session.status} />
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} />{sessionWhen(session)}</span>
          <span>{SESSION_MODES[session.mode]}</span>
          {showPharmacy && session.pharmacy?.name && <span>{session.pharmacy.name}</span>}
          {typeof session.participant_count === "number" && (
            <span className="inline-flex items-center gap-1"><Users size={12} />
              {session.status === "closed" ? `${session.attended_count} présent${session.attended_count > 1 ? "s" : ""} sur ${session.participant_count}`
                : `${session.participant_count} participant${session.participant_count > 1 ? "s" : ""}`}
            </span>
          )}
          <span className="text-slate-400">{session.reference}</span>
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0" />
    </Link>
  );
}

// « Sessions » tab of the Formations page. `mine` (from the dashboard) shows only the sessions I train.
export default function SessionsPanel({ search, mine, onMeta, exportOpen, onExportOpenChange }) {
  const [view, setView] = useState("open");
  const [state, setState] = useState({ phase: "loading", items: [], error: "" });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: s.items.length ? "ready" : "loading" }));
    try {
      const statuses = VIEWS.find(([key]) => key === view)[2];
      const { data } = await api.get("/training/sessions", { params: { status: statuses, ...(mine ? { mine: true } : {}) }, paramsSerializer: { indexes: null } });
      setState({ phase: "ready", items: data.items, error: "" });
      onMeta?.({ canCreate: data.can_create_without_mission });
    } catch (e) {
      setState({ phase: "error", items: [], error: trainingError(e, "Impossible de charger les sessions.") });
    }
  }, [view, mine, onMeta]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => orderSessions(filterByWords(state.items, search,
    (s) => `${s.reference} ${s.program.label} ${s.pharmacy?.name || ""} ${(s.trainers || []).map((t) => t.name).join(" ")}`)), [state.items, search]);

  return (
    <div className="space-y-3">
      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Sessions">
        {VIEWS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)} data-testid={`sessions-view-${key}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium ${view === key ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>
      {mine && <p className="text-xs text-slate-500" data-testid="sessions-mine">Les sessions dont je suis formateur.</p>}
      {state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {state.phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {state.phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="sessions-empty">
          <CalendarDays className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{search ? "Aucune session ne correspond" : view === "open" ? "Aucune session prévue" : "Aucune session"}</h3>
          {view === "open" && !search && (
            <p className="text-sm text-slate-500 max-w-md mx-auto">Une session sur place se prépare depuis la mission (carte « Formations ») ; une visio ou
              une formation interne, depuis « Nouvelle session ».</p>
          )}
        </Card>
      )}
      {state.phase === "ready" && items.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="training-sessions">
          {items.map((s) => <SessionRow key={s.id} session={s} />)}
        </Card>
      )}
      <ExportDialog open={!!exportOpen} onOpenChange={onExportOpenChange} />
    </div>
  );
}

function monthBounds() {
  const now = new Date();
  const first = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const last = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
  return [first.toISOString().slice(0, 10), last.toISOString().slice(0, 10)];
}

function ExportDialog({ open, onOpenChange }) {
  const [range, setRange] = useState(monthBounds);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/training/sessions/export.csv", { params: { from: range[0], to: range[1] }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `formations-${range[0]}-${range[1]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onOpenChange(false);
    } catch (e) {
      toast.error("Impossible de télécharger l'export.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sessions-export-dialog">
        <DialogHeader>
          <DialogTitle>Exporter les sessions</DialogTitle>
          <DialogDescription>Une ligne par participant : présence, modules réussis, objectifs acquis. Jamais de coordonnées.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label htmlFor="export-from">Du</Label>
            <Input id="export-from" type="date" className="text-base md:text-sm" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} /></div>
          <div className="space-y-1.5"><Label htmlFor="export-to">Au</Label>
            <Input id="export-to" type="date" className="text-base md:text-sm" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} /></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={download} disabled={busy || !range[0] || !range[1] || range[0] > range[1]} data-testid="sessions-export-confirm">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}Télécharger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
